import React, { useState, useEffect } from 'react';
import { ApiDetails, SearchParameterType } from '../../types';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import {
  getParameterTypeTemplate,
  generateClonedApiName,
  adjustParametersForType,
  getAvailableParameterTypes
} from '../../config/parameterTypeTemplates';

interface CloneApiModalProps {
  isOpen: boolean;
  sourceApi: ApiDetails;
  availableParameterTypes: SearchParameterType[];
  existingApis: ApiDetails[];
  onClose: () => void;
  onClone: (clonedApiData: Partial<ApiDetails>) => Promise<void>;
}

export const CloneApiModal: React.FC<CloneApiModalProps> = ({
  isOpen,
  sourceApi,
  availableParameterTypes,
  existingApis,
  onClose,
  onClone
}) => {
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [apiName, setApiName] = useState('');
  const [queryFormat, setQueryFormat] = useState('');
  const [price, setPrice] = useState(5);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [requestParameters, setRequestParameters] = useState<Record<string, any>>({});
  const [countryCodeFormat, setCountryCodeFormat] = useState<string>('none');
  const [isCloning, setIsCloning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get available types (exclude already cloned types)
  const availableTypes = getAvailableParameterTypes(
    availableParameterTypes,
    sourceApi,
    existingApis
  );

  // Get selected parameter type object
  const selectedType = availableTypes.find(type => type.id === selectedTypeId);

  // Update form when parameter type changes
  useEffect(() => {
    if (selectedTypeId) {
      const type = availableTypes.find(t => t.id === selectedTypeId);
      if (type) {
        const template = getParameterTypeTemplate(type.name);
        
        if (template) {
          // Generate API name
          setApiName(generateClonedApiName(sourceApi.name, type.name));
          
          // Set query format
          setQueryFormat(template.queryFormat);
          
          // Set suggested price
          setPrice(template.suggestedPrice);
          
          // Set country code format
          setCountryCodeFormat(template.countryCodeFormat || 'none');
          
          // Adjust request parameters
          const adjustedParams = adjustParametersForType(
            sourceApi.requestParameters || {},
            template
          );
          setRequestParameters(adjustedParams);
        }
      }
    }
  }, [selectedTypeId, sourceApi, availableTypes]);

  const handleClone = async () => {
    if (!selectedTypeId) {
      setError('Please select a parameter type');
      return;
    }

    setIsCloning(true);
    setError(null);

    try {
      // Build cloned API configuration
      const clonedData: Partial<ApiDetails> = {
        name: apiName,
        description: sourceApi.description,
        baseUrl: sourceApi.baseUrl,
        requestMethod: sourceApi.requestMethod,
        headers: { ...sourceApi.headers },
        urlPattern: sourceApi.urlPattern,
        parameterFormat: sourceApi.parameterFormat,
        parameterName: sourceApi.parameterName,
        organizationId: sourceApi.organizationId,
        searchParameterTypeId: selectedTypeId,
        status: 'active',
        price: price,
        requestParameters: requestParameters,
        countryCodeFormat: countryCodeFormat as any,
        apiDocumentationUrl: sourceApi.apiDocumentationUrl
      };

      await onClone(clonedData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to clone API');
    } finally {
      setIsCloning(false);
    }
  };

  const handleParameterChange = (key: string, value: any) => {
    setRequestParameters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleRemoveParameter = (key: string) => {
    setRequestParameters(prev => {
      const newParams = { ...prev };
      delete newParams[key];
      return newParams;
    });
  };

  if (!isOpen) return null;

  const template = selectedType ? getParameterTypeTemplate(selectedType.name) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Clone API for Different Parameter Type</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Cloning from: {sourceApi.name}</p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* What will be copied */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">✓ What will be copied:</h4>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>• Base URL and Headers (API Keys)</li>
              <li>• Request Method and Configuration</li>
              <li>• Organization and Basic Settings</li>
            </ul>
          </div>

          {/* Parameter Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              New Parameter Type *
            </label>
            <select
              value={selectedTypeId || ''}
              onChange={(e) => setSelectedTypeId(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select parameter type...</option>
              {availableTypes.map(type => {
                const tmpl = getParameterTypeTemplate(type.name);
                return (
                  <option key={type.id} value={type.id}>
                    {tmpl?.icon || ''} {type.display_name} - {tmpl?.description || type.description}
                  </option>
                );
              })}
            </select>
            {availableTypes.length === 0 && (
              <p className="text-sm text-amber-600 mt-2">
                ⚠️ All parameter types have already been configured for this API
              </p>
            )}
          </div>

          {/* Configuration Preview */}
          {selectedTypeId && template && (
            <>
              {/* API Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  API Name *
                </label>
                <input
                  type="text"
                  value={apiName}
                  onChange={(e) => setApiName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="API name"
                />
              </div>

              {/* Query Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Query Format
                </label>
                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Format:</span>
                    <code className="text-sm bg-white dark:bg-gray-800 px-3 py-1 rounded border border-gray-300 dark:border-gray-600">
                      {queryFormat}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Example:</span>
                    <code className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded border border-green-200 dark:border-green-800">
                      {template.exampleValue}
                    </code>
                  </div>
                </div>
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Price (Credits) *
                </label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Suggested: {template.suggestedPrice} credits</p>
              </div>

              {/* Advanced Options */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 font-medium"
                >
                  {showAdvanced ? '▼' : '▶'} Advanced Options
                </button>

                {showAdvanced && (
                  <div className="mt-4 space-y-4 border-t pt-4">
                    {/* Country Code Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Country Code Format
                      </label>
                      <select
                        value={countryCodeFormat}
                        onChange={(e) => setCountryCodeFormat(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="none">No Country Code</option>
                        <option value="numeric">Numeric (91)</option>
                        <option value="plus_prefix">Plus Prefix (+91)</option>
                        <option value="iso_code">ISO Code (IN)</option>
                      </select>
                    </div>

                    {/* Request Parameters */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Request Parameters
                      </label>
                      <div className="space-y-2">
                        {Object.entries(requestParameters).map(([key, value]) => (
                          <div key={key} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={key}
                              disabled
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm"
                            />
                            <input
                              type="text"
                              value={typeof value === 'string' ? value : JSON.stringify(value)}
                              onChange={(e) => handleParameterChange(key, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => handleRemoveParameter(key)}
                              className="px-3 py-2 text-sm"
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Configuration Changes Summary */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-900 mb-2">Configuration Summary:</h4>
                <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                  <li>• Parameter Type: {selectedType?.display_name}</li>
                  <li>• Query Format: {queryFormat}</li>
                  <li>• Price: {price} credits</li>
                  <li>• Country Code: {countryCodeFormat}</li>
                  <li>• Request Parameters: {Object.keys(requestParameters).length} params</li>
                </ul>
              </div>
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isCloning}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleClone}
            disabled={!selectedTypeId || isCloning || availableTypes.length === 0}
          >
            {isCloning ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Cloning...
              </>
            ) : (
              'Clone API'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

